import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription, Observable, skipWhile, withLatestFrom } from 'rxjs';
import { EDeviceCategory } from 'src/app/conf/device-categories';
import { EDeviceModel, isMtmacModel } from 'src/app/conf/device-models';
import { supportInfoNotif, supportWarnNotif, supportErrNotif, Device } from 'src/app/model/device';
import { MailNotificationConfig } from 'src/app/model/mail-notification-config';
import * as EventActions from 'src/app/store/event/event.actions';

import { AppState } from 'src/app/store/app.reducer';
import { selecMtmaModules, selectCurrentDevice, selectCurrentDeviceStatus } from 'src/app/store/device/device.selector';
import { MtmaModule, MtmaModuleConfig } from 'src/app/model/mtma-module';
import { MtmaModuleType } from 'src/app/conf/mtma-module';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { DeviceStatus } from 'src/app/model/device-status';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';


@Component({
	selector: 'app-notifications',
	templateUrl: './notifications.component.html',
	providers: [],
})
export class NotificationsComponent implements OnInit, OnDestroy {

	mtmaModules$: Observable<MtmaModule[] | undefined>;
	mtmaModulesSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;

	emailConfigsForm: FormGroup;

	infoNotif = supportInfoNotif;
	warnNotif = supportWarnNotif;
	errNotif = supportErrNotif;

	loading = false;
	configured: boolean;
	online: boolean;

	device: Device;

	emailConfigSub: Subscription;
	emailConfigs$: Observable<MailNotificationConfig[]>;
	currentDeviceEmailConfigs: MailNotificationConfig[];

	// design
	screenWidth: number;
	MIN_MD_WIDTH = 500;

	constructor(
		private store: Store<AppState>,
		private fb: FormBuilder,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.mtmaModules$ = this.store.select(selecMtmaModules);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
	}

	ngOnInit(): void {
		this.emailConfigsForm = this.fb.group({
			info: [{ value: false, disabled: this.device && this.infoNotif(this.device) }],
			warning: [{ value: false, disabled: this.device && this.warnNotif(this.device) }],
			error: [{ value: false, disabled: this.device && this.errNotif(this.device) }],
		});
		// this.store.dispatch(EventActions.getEmailConfigs());
		this.emailConfigs$ = this.store.select('event', 'emailConfigs');
		this.emailConfigSub = this.emailConfigs$.pipe(
			skipWhile(c => !c),
			withLatestFrom(this.store.select(selectCurrentDevice))
		).subscribe(([emailConfigs, device]) => {
			this.device = device;
			this.currentDeviceEmailConfigs = emailConfigs.filter(c => c.DeviceId === device.Id);
			if (this.isMTMAConnect()) {// Custom for MTMAC
				this.mtmaModulesSub = this.mtmaModules$.subscribe(mtmaModules => {
					const module = mtmaModules.filter(item => item.Type === MtmaModuleType.MTMAC)[0];
					if (module !== undefined && module.Config !== module.Config !== undefined && module.Config.In1Notify !== undefined) {
						this.f.info.setValue(module.Config.In1Notify === 1);
					} else {
						this.f.info.setValue(0);
					}
				});
			} else {
				this.f.info.setValue(!!this.currentConfigLevel(1));
				this.f.warning.setValue(!!this.currentConfigLevel(2));
				this.f.error.setValue(!!this.currentConfigLevel(3));
			}
		});
		this.deviceStatusSub = this.deviceStatus$.subscribe(deviceState => {
			if (deviceState) {
				this.configured = deviceState.Configured === 0;
				this.online = deviceState.Online;
				if (this.online && this.configured) {
					this.f.info.enable();
				} else {
					this.f.info.disable();
				}

			}
		});
		this.onResize();
	}

	ngOnDestroy(): void {
		this.emailConfigSub?.unsubscribe();
		this.mtmaModulesSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
	}

	currentConfigLevel(level: number): MailNotificationConfig {
		return this.currentDeviceEmailConfigs.find(c => c.NotifyLevel === level);
	}

	setInfo(checked: boolean): void {
		this.submit(1, checked);
	}

	setWarn(checked: boolean): void {
		this.submit(2, checked);
	}

	setErr(checked: boolean): void {
		this.submit(3, checked);
	}

	supportInfo(): boolean {
		return this.device?.CategoryId === EDeviceCategory.Virtual || this.device?.ModelId === EDeviceModel.RBE4 ||
			this.device?.ModelId === EDeviceModel.RBE4030 || this.device?.ModelId === EDeviceModel.RBE4230 ||
			this.device?.ModelId === EDeviceModel.GEN_RBE4;
	}
	supportWarning(): boolean {
		return this.device?.CategoryId === EDeviceCategory.Automation &&
			this.device?.ModelId !== EDeviceModel.IORS485 && this.device?.ModelId !== EDeviceModel.GEN_IORS485;
	}

	supportError(): boolean {
		return (this.device?.CategoryId === EDeviceCategory.Automation) &&
			([EDeviceModel.RBE4, EDeviceModel.RBE4030, EDeviceModel.RBE4230, EDeviceModel.GEN_RBE4,
			EDeviceModel.RBE230, EDeviceModel.RBE24, EDeviceModel.RBE24230, EDeviceModel.GEN_RBE24230,
			EDeviceModel.IORS485, EDeviceModel.GEN_IORS485].includes(this.device?.ModelId) === false);
	}

	submit(level: number, value: boolean): void {
		if (this.isMTMAConnect()) {// Custom for MTMAC
			const module = new MtmaModuleConfig();
			module.In1Notify = value ? 1 : 0;
			this.store.dispatch(DeviceActions.editParamMtmaModule({ module: module }));
		} else {
			if (value === true) {
				this.store.dispatch(EventActions.saveEmailConfigs({ level }));
			} else {
				const config = this.currentConfigLevel(level);
				if (config) {
					this.store.dispatch(EventActions.deleteEmailConfigs({ configId: config.Id }));
				}
			}
		}
	}

	isMTMAConnect(): boolean {
		if (this.device === undefined) {
			return false;
		}
		return isMtmacModel(this.device.ModelId);
	}


	get f(): any {
		return this.emailConfigsForm.controls;
	}

	// Design
	@HostListener('window:resize')
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile() {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}
}
